import { useCallback } from 'react';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import { useI18n, useCurrentLocale } from '@locales/client';
import { Image } from '@/shared/ui/image';
import { DeleteFilterElement } from './styles';
import { useProviders } from '@/features/select-provider/model/hooks';
import { processText } from '@/shared/libs/process-text';

const CATEGORY_MAPPING = {
  slots: 'slots',
  instant: 'fast',
  table: 'table',
  live: 'dealer',
  betting: 'betting',
} as const;

const TYPE_OF_CATEGORIES = Object.keys(CATEGORY_MAPPING);

export const DeleteFilter = () => {
  const router = useRouter();
  const { providers, isLoading } = useProviders();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const t = useI18n();
  const locale = useCurrentLocale();

  const slots = pathname.match(/\/games\/([^/]+)/)?.[1];
  const filterParam = searchParams.get('filter');
  const searchParam = searchParams.get('search');
  const favoritParam = slots === 'favorite';
  const lotteryParam = slots === 'lottery';

  const handleDeleteAll = useCallback(() => {
    router.push(`/${locale}/games`);
  }, [router]);

  const renderContent = useCallback(() => {
    const activeFilters = [];
    const providerTitle = providers?.find(
      (el) => processText(el?.id) === slots
    )?.title;

    if (favoritParam) {
      activeFilters.push(t('mini_games_banners.wishlist'));
    }
    
    if (lotteryParam) {
      activeFilters.push(t('mini_games_banners.lottery'));
    }

    if (searchParam) {
      const truncatedValue =
        searchParam.length > 8 ? `${searchParam.slice(0, 8)}...` : searchParam;
      activeFilters.push(
        `${t('common.filter.tags.search')} “${truncatedValue}”`
      );
    }

    if (filterParam) {
      const translationKey = `common.filter.category.${filterParam}`;
      const translation = t(translationKey);
      activeFilters.push(
        translation === translationKey ? filterParam : translation
      );
    }

    if (slots && TYPE_OF_CATEGORIES.includes(slots)) {
      activeFilters.push(t(`mini_games_banners.${CATEGORY_MAPPING[slots]}`));
    } else if (slots && providerTitle) {
      activeFilters.push(providerTitle);
    }

    return activeFilters.map((filter, index) => (
      <span key={index}>
        {filter}
        {index < activeFilters.length - 1 && ', '}
      </span>
    ));
  }, [slots, favoritParam, lotteryParam,searchParam, filterParam, t, providers]);

  const hasActiveFilters =
    favoritParam || searchParam || filterParam || (slots && slots !== 'all');

  if (!hasActiveFilters) return null;

  return (
    <DeleteFilterElement onClick={handleDeleteAll}>
      {renderContent()}
      <Image
        style={{ display: 'flex' }}
        width={16}
        height={16}
        alt="close-icon"
        src="/images/icons/filter-close.svg"
      />
    </DeleteFilterElement>
  );
};
